import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { useSSE } from "@/hooks/useSSE";
import { usePresence } from "@/hooks/usePresence";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AutonomousActivityIndicator } from "@/components/AutonomousActivityIndicator";
import { HierarchyBreadcrumb } from "@/components/v2/HierarchyBreadcrumb";
import { CrossOrgGate } from "@/components/CrossOrgGate";
import { Button } from "@/components/ui/button";

/**
 * Layout wrapper for project-scoped routes (`/projects/:projectId/*`).
 * Resolves the project from the URL and renders child routes via <Outlet />.
 * The actual shell (Sidebar, Topbar) is handled by AppShell above this.
 * Also connects to the SSE stream for real-time updates and presence tracking.
 *
 * Row 2b6d6836: a 404 from GET /projects/{id} (a link to a project in another
 * org, or one that does not exist) renders CrossOrgGate instead of the
 * project pages — never the pages with empty, wrong-org data.
 *
 * WHILE THAT REQUEST IS IN FLIGHT THE PAGE RENDERS (row 4bd890ab). Holding
 * the Outlet behind a spinner until the project answered made every project
 * page a waterfall: project request, THEN the page's lazy chunk, THEN its
 * data. On a loaded CI runner that left <main> empty for seconds after the
 * network went idle, and the a11y content floor failed tasks, modules,
 * components, baselines and the project home on it. The page's chunk and data
 * now load beside the project request, as they did before the gate; a 404
 * still swaps the page for the gate when it lands.
 */
export function ProjectLayout() {
  // This hook keeps the project store in sync with the URL param
  const { project, error } = useCurrentProject();
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const notHere = !project && (error as { status?: number } | null)?.status === 404;

  // SSE + presence only for a project this org can see.
  const liveId = project ? projectId : undefined;
  useSSE(liveId);
  usePresence(liveId);

  if (notHere && projectId) {
    return (
      <CrossOrgGate
        kind="project"
        entityId={projectId}
        onCancel={() => navigate("/projects")}
        notFound={
          <div className="mx-auto max-w-md py-16 text-center" data-testid="project-not-found">
            <h2 className="text-lg font-semibold">Project not found or you don't have access</h2>
            <Button className="mt-6" variant="outline" onClick={() => navigate("/projects")}>
              Back to projects
            </Button>
          </div>
        }
      />
    );
  }
  return (
    <ErrorBoundary>
      {/* Phase 4 (UX v2): Portfolio > Program > Project breadcrumb.
          Renders only when ftl.ux.v2.portfolios / .programs are on AND
          the project actually has a program_id; otherwise the component
          returns null and the layout collapses. */}
      {projectId && (
        <HierarchyBreadcrumb
          projectId={projectId}
          className="mb-3"
        />
      )}
      <Outlet />
      <AutonomousActivityIndicator />
    </ErrorBoundary>
  );
}
